import get from 'lodash/get';

import { ProposalDAOInterface } from '../../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../../ProposalDAOImpl';
import { ComparisonItemDAO } from '../../ProposalItemDAO';
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';
import { PercentageCell, TextCell, HTMLCell } from '../../../../../views/components/cells';

// Utils
import { formatLocalizedString } from '../../../../../utils/i18n';
import ProposalTableData from '@/types/proposal-table-data';
import ProposalTableRowData from '@/types/proposal-table-row-data';
import ComparisonHolding from '@/types/comparison-holding';

export default class AllocationAnalysisDAO extends ComparisonItemBasedProposalDAO {
  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new AllocationAnalysisDAO(proposal, proposalElement);
  }

  data: ProposalTableData = { rows: [] };

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return [];
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected build(): void {
    let itemDAO: ComparisonItemDAO | null = null;
    const daoConfig: ProposalNS.AllocationAnalysis = this.proposalElement.DAO as ProposalNS.AllocationAnalysis;

    const rows: ProposalTableRowData[] = [];
    const header: ProposalTableRowData = { data: [] };

    const concatFields = daoConfig.fields.concat(daoConfig.fields);
    for (const field of concatFields) {
      header.data.push({
        value: formatLocalizedString(field.label),
        className: field.className,
      });
    }

    while ((itemDAO = this.nextItem()) !== null) {
      const { holdings } = itemDAO.item;
      const colCount = 2;
      // Remove holdings with a weight >= 0.5
      const filteredHoldings = holdings.filter(holding => holding.weight >= 0.5);
      const rearrangedHoldings = this.rearrangeHoldings(filteredHoldings, colCount);
      const chunkedHoldings = this.chunkArray(rearrangedHoldings, colCount);

      for (const chunkedHolding of chunkedHoldings) {
        const rowData = [];

        for (let i = 0; i < colCount; i++) {
          for (const field of daoConfig.fields) {
            if (chunkedHolding[i]) {
              let decimalPlace = 0;
              const comparisonHolding: ComparisonHolding = chunkedHolding[i];
              const { dataPaths } = field;

              const data = dataPaths.map(dataPath => {
                let val = get(comparisonHolding, dataPath.path) || '';

                if (dataPath.selection != null) {
                  val = val[dataPath.selection];
                }

                return val;
              });
              let value;
              let cellType: any = TextCell;
              let { className } = field;

              // If we are outputing multiple values in a cell, make sure that cell is of type HTMLCell
              // and add minimal markup
              if (data.length > 1) {
                cellType = HTMLCell;
                value = data.map(val => `<span>${formatLocalizedString(val) || val}</span>`).join('<br />');
              } else {
                value = formatLocalizedString(data[0]) || data[0];
                switch (field.cellType) {
                  case 'IMAGE':
                    value = value ? `<img src="${value}" />` : '';
                    cellType = HTMLCell;
                    break;
                  case 'PERCENTAGE':
                    cellType = PercentageCell;
                    decimalPlace = 2;
                    break;
                  case 'TEXT':
                    cellType = TextCell;
                    className += ` ${value || ''}`;
                }
              }
              rowData.push({
                cellType,
                decimalPlace,
                value,
                className,
              });
            } else {
              rowData.push({
                cellType: TextCell,
                value: '',
                className: field.className,
              });
            }
          }
        }

        rows.push({
          data: rowData,
        });
      }
    }

    this.data = {
      header,
      rows,
    };
  }

  getTableClassName(): string {
    return 'allocation-analysis';
  }

  getTableData(): ProposalTableData {
    return this.data;
  }

  // private methods
  private chunkArray(input: ComparisonHolding[], chunkSize: number) {
    return input.reduce(
      (acc, item, index) => {
        const chunkIndex = Math.floor(index / chunkSize);

        if (!acc[chunkIndex]) {
          acc[chunkIndex] = []; // Start a new chunk
        }

        acc[chunkIndex].push(item);

        return acc;
      },
      [[]] as ComparisonHolding[][],
    );
  }

  private rearrangeHoldings(holdings: ComparisonHolding[], colCount: number) {
    const chunkSize = Math.ceil(holdings.length / colCount);
    const chunkedHoldings = this.chunkArray(holdings, chunkSize);

    const out = [];
    for (let i = 0; i < chunkSize; i++) {
      for (let j = 0; j < colCount; j++) {
        if (chunkedHoldings[j].length === 1 && chunkedHoldings.length < colCount) {
          // Add the holding to the array and break out of the loop
          // when the number of holdings to be outputted is less than the column count
          out.push(chunkedHoldings[j][i]);
          break;
        }

        if (chunkedHoldings[j][i] === undefined) {
          break;
        }

        out.push(chunkedHoldings[j][i]);
      }
    }

    return out;
  }
}
